import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { ASSETS } from "../config/assets";

const DEFAULT_BUTTON = {
  phone: "",
  background: "#25D366",
  ctaText: "Chat with us",
  marginBottom: 20,
  marginLeft: 20,
  marginRight: 20,
  borderRadius: 30,
  prefilledMsg: "Hi! I have a question",
  position: "bottom-right",
};

const DEFAULT_WIDGET = {
  brandName: "Your Brand",
  subtitle: "online",
  brandColor: "#075E54",
  widgetCTA: "Start chat",
  onscreenMsg: "Hi,\nHow can I help you?",
  brandImageUrl: "",
  openOnMobile: true,
  openByDefault: false,
  reopenBehavior: "always",
  newsImageUrl: "",
  newsTitle: "",
  newsText: "",
  emailText: "",
  phoneText: "",
};

const TOOLTIPS = {
  phone: "Enter WhatsApp number with country code, e.g. 911234567890",
  ctaText: "Text shown on the chat button",
  marginBottom: "Distance from bottom",
  marginLeft: "Distance from left (if bottom-left)",
  marginRight: "Distance from right (if bottom-right)",
  borderRadius: "Corner rounding of button",
  prefilledMsg: "Message pre-filled when user starts chat",
  brandName: "Your brand or business name",
  subtitle: "Small text under brand name",
  brandColor: "Color for header background",
  brandImageUrl: "Logo/image URL shown in header",
  widgetCTA: "Text on widget action button",
  onscreenMsg: "Message shown inside chat popup",
  newsTitle: "Title of the news",
  newsText: "Main Summary of News",
  emailText: "Email CTA",
  phoneText: "Phone CTA",
};

const Field = ({ label, name, value, onChange, type = "text", multiline }) => (
  <label className="wa-widget-field">
    <span className="wa-widget-label">
      {label}
      {TOOLTIPS[name] && (
        <span className="wa-widget-tip" title={TOOLTIPS[name]}>
          ?
        </span>
      )}
    </span>
    {multiline ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
      />
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} />
    )}
  </label>
);

const WhatsAppWidgetButton = () => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("chat");
  const [newsModal, setNewsModal] = useState(false);
  const [snippet, setSnippet] = useState("");
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [buttonSettings, setButtonSettings] = useState(DEFAULT_BUTTON);
  const [widgetSettings, setWidgetSettings] = useState(DEFAULT_WIDGET);

  useEffect(() => {
    if (widgetSettings.openByDefault) setPreviewOpen(true);
  }, [widgetSettings.openByDefault]);

  const onButtonChange = (e) => {
    const { name, value } = e.target;
    setButtonSettings((prev) => ({ ...prev, [name]: value }));
  };

  const onWidgetChange = (e) => {
    const { name, value } = e.target;
    let parsed = value;
    if (value === "true") parsed = true;
    if (value === "false") parsed = false;
    setWidgetSettings((prev) => ({ ...prev, [name]: parsed }));
  };

  const allFilled = () => {
    const merged = { ...buttonSettings, ...widgetSettings };
    return Object.entries(merged).every(([, v]) =>
      typeof v === "boolean" ? true : v != null && v !== "",
    );
  };

  const generateSnippet = () => {
    if (!allFilled()) {
      window.alert("Please fill all fields before generating snippet.");
      return;
    }
    const config = JSON.stringify({ buttonSettings, widgetSettings }).replace(
      /'/g,
      "&apos;",
    );
    setSnippet(
      `<script src="${ASSETS.brandDomain.replace(/\/$/, "")}/widget.js" data-config='${config}'></` +
        "script>",
    );
    setSnippetOpen(true);
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      window.alert("Snippet copied to clipboard.");
    } catch {
      window.alert("Copy failed — select the snippet manually.");
    }
  };

  const waLink = () => {
    const text = encodeURIComponent(buttonSettings.prefilledMsg || "");
    return `https://wa.me/${buttonSettings.phone}?text=${text}`;
  };

  const pos = buttonSettings.position === "bottom-left";

  return (
    <div className="wa-widget-page">
      <header className="wa-widget-hero">
        <FaWhatsapp className="wa-widget-hero-icon" />
        <h1>WhatsApp Live Chat Widgets</h1>
      </header>

      <section className="wa-widget-steps">
        <h2>3 steps to creating a WhatsApp Live Chat Widget</h2>
        <div className="wa-widget-steps-grid">
          <div className="wa-widget-step wa-widget-step-pink">
            <h3>Step #1</h3>
            <p>
              Design, style and color your button, then name it to set the Call
              to Action and position it.
            </p>
          </div>
          <div className="wa-widget-step wa-widget-step-blue">
            <h3>Step #2</h3>
            <p>
              Brand your widget, add phone number and welcome text and
              automatically embed the page URL that has sparked the chat.
            </p>
          </div>
          <div className="wa-widget-step wa-widget-step-yellow">
            <h3>Step #3</h3>
            <p>
              Finally, click on the &apos;Generate Widget Code&apos; button.
              It&apos;s easy!
            </p>
          </div>
        </div>
      </section>

      <div className="wa-widget-builder">
        <section className="wa-widget-card">
          <h2>Chat Button Settings</h2>
          <div className="wa-widget-grid">
            <Field
              label="WhatsApp Phone Number"
              name="phone"
              value={buttonSettings.phone}
              onChange={onButtonChange}
            />
            <Field
              label="CTA Text"
              name="ctaText"
              value={buttonSettings.ctaText}
              onChange={onButtonChange}
            />
            <Field
              label="Margin Bottom"
              name="marginBottom"
              type="number"
              value={buttonSettings.marginBottom}
              onChange={onButtonChange}
            />
            <Field
              label="Margin Left"
              name="marginLeft"
              type="number"
              value={buttonSettings.marginLeft}
              onChange={onButtonChange}
            />
            <Field
              label="Margin Right"
              name="marginRight"
              type="number"
              value={buttonSettings.marginRight}
              onChange={onButtonChange}
            />
            <Field
              label="Border Radius"
              name="borderRadius"
              type="number"
              value={buttonSettings.borderRadius}
              onChange={onButtonChange}
            />
            <Field
              label="Prefilled Message"
              name="prefilledMsg"
              value={buttonSettings.prefilledMsg}
              onChange={onButtonChange}
            />
            <label className="wa-widget-field">
              <span className="wa-widget-label">Button Color</span>
              <input
                type="color"
                name="background"
                value={buttonSettings.background}
                onChange={onButtonChange}
              />
            </label>
            <div className="wa-widget-field">
              <span className="wa-widget-label">Position</span>
              <div className="wa-widget-radios">
                <label>
                  <input
                    type="radio"
                    name="position"
                    value="bottom-right"
                    checked={buttonSettings.position === "bottom-right"}
                    onChange={onButtonChange}
                  />
                  Right
                </label>
                <label>
                  <input
                    type="radio"
                    name="position"
                    value="bottom-left"
                    checked={buttonSettings.position === "bottom-left"}
                    onChange={onButtonChange}
                  />
                  Left
                </label>
              </div>
            </div>
          </div>
        </section>

        <section className="wa-widget-card">
          <h2>Chat Widget Settings</h2>
          <div className="wa-widget-grid">
            <Field
              label="Brand Name"
              name="brandName"
              value={widgetSettings.brandName}
              onChange={onWidgetChange}
            />
            <Field
              label="Subtitle"
              name="subtitle"
              value={widgetSettings.subtitle}
              onChange={onWidgetChange}
            />
            <Field
              label="Widget CTA"
              name="widgetCTA"
              value={widgetSettings.widgetCTA}
              onChange={onWidgetChange}
            />
            <label className="wa-widget-field">
              <span className="wa-widget-label">Brand Color</span>
              <input
                type="color"
                name="brandColor"
                value={widgetSettings.brandColor}
                onChange={onWidgetChange}
              />
            </label>
            <Field
              label="Brand Image URL"
              name="brandImageUrl"
              value={widgetSettings.brandImageUrl}
              onChange={onWidgetChange}
            />
            <Field
              label="Onscreen Message"
              name="onscreenMsg"
              value={widgetSettings.onscreenMsg}
              onChange={onWidgetChange}
              multiline
            />
          </div>

          <div className="wa-widget-grid wa-widget-grid-2">
            <div className="wa-widget-field">
              <span className="wa-widget-label">Open on mobile</span>
              <div className="wa-widget-radios">
                <label>
                  <input
                    type="radio"
                    name="openOnMobile"
                    value="true"
                    checked={widgetSettings.openOnMobile === true}
                    onChange={onWidgetChange}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="openOnMobile"
                    value="false"
                    checked={widgetSettings.openOnMobile === false}
                    onChange={onWidgetChange}
                  />
                  No
                </label>
              </div>
            </div>
            <div className="wa-widget-field">
              <span className="wa-widget-label">Open by default</span>
              <div className="wa-widget-radios">
                <label>
                  <input
                    type="radio"
                    name="openByDefault"
                    value="true"
                    checked={widgetSettings.openByDefault === true}
                    onChange={onWidgetChange}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="openByDefault"
                    value="false"
                    checked={widgetSettings.openByDefault === false}
                    onChange={onWidgetChange}
                  />
                  No
                </label>
              </div>
            </div>
          </div>

          {widgetSettings.openByDefault && (
            <div className="wa-widget-field">
              <span className="wa-widget-label">Reopen Behavior</span>
              <div className="wa-widget-radios">
                <label>
                  <input
                    type="radio"
                    name="reopenBehavior"
                    value="always"
                    checked={widgetSettings.reopenBehavior === "always"}
                    onChange={onWidgetChange}
                  />
                  Always
                </label>
                <label>
                  <input
                    type="radio"
                    name="reopenBehavior"
                    value="24hr"
                    checked={widgetSettings.reopenBehavior === "24hr"}
                    onChange={onWidgetChange}
                  />
                  After 24hr
                </label>
              </div>
            </div>
          )}

          <h3 className="wa-widget-section-title">News Section</h3>
          <div className="wa-widget-grid">
            <Field
              label="News Image URL"
              name="newsImageUrl"
              value={widgetSettings.newsImageUrl}
              onChange={onWidgetChange}
            />
            <Field
              label="News Title"
              name="newsTitle"
              value={widgetSettings.newsTitle}
              onChange={onWidgetChange}
            />
            <Field
              label="News Text"
              name="newsText"
              value={widgetSettings.newsText}
              onChange={onWidgetChange}
              multiline
            />
          </div>

          <h3 className="wa-widget-section-title">Contact Options</h3>
          <div className="wa-widget-grid">
            <Field
              label="Email CTA Text"
              name="emailText"
              value={widgetSettings.emailText}
              onChange={onWidgetChange}
            />
            <Field
              label="Phone CTA Text"
              name="phoneText"
              value={widgetSettings.phoneText}
              onChange={onWidgetChange}
            />
          </div>
        </section>

        <div className="wa-widget-actions">
          <button
            type="button"
            className="biz-btn-primary"
            onClick={generateSnippet}
          >
            Generate Snippet
          </button>
        </div>
      </div>

      {snippetOpen && (
        <div className="wa-widget-snippet-modal">
          <div className="wa-widget-snippet-box">
            <h3>Embed code</h3>
            <textarea readOnly value={snippet} rows={5} />
            <div className="wa-widget-snippet-btns">
              <button type="button" className="biz-btn-primary" onClick={copySnippet}>
                Copy
              </button>
              <button
                type="button"
                className="biz-btn-secondary"
                onClick={() => setSnippetOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="wa-widget-preview-fab"
        style={{
          bottom: `${buttonSettings.marginBottom}px`,
          right: pos ? "unset" : `${buttonSettings.marginRight}px`,
          left: pos ? `${buttonSettings.marginLeft}px` : "unset",
        }}
      >
        {previewOpen && (
          <div
            className="wa-widget-preview-popup"
            style={{
              right: pos ? "unset" : 0,
              left: pos ? 0 : "unset",
            }}
          >
            <div
              className="wa-widget-preview-header"
              style={{ backgroundColor: widgetSettings.brandColor }}
            >
              <div className="wa-widget-preview-brand">
                {widgetSettings.brandImageUrl && (
                  <img src={widgetSettings.brandImageUrl} alt="" />
                )}
                <div>
                  <strong>{widgetSettings.brandName}</strong>
                  <small>{widgetSettings.subtitle}</small>
                </div>
              </div>
              <button type="button" onClick={() => setPreviewOpen(false)}>
                ×
              </button>
            </div>
            <div className="wa-widget-preview-body">
              {previewMode === "chat" ? (
                <div className="wa-widget-preview-chat">
                  <strong>{widgetSettings.brandName}</strong>
                  <p style={{ whiteSpace: "pre-wrap" }}>
                    {widgetSettings.onscreenMsg}
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  className="wa-widget-preview-news"
                  onClick={() => setNewsModal(true)}
                >
                  {widgetSettings.newsImageUrl && (
                    <img src={widgetSettings.newsImageUrl} alt="" />
                  )}
                  <h4>{widgetSettings.newsTitle}</h4>
                  <p>{widgetSettings.newsText}</p>
                </button>
              )}
            </div>
            <div className="wa-widget-preview-footer">
              <button
                type="button"
                onClick={() =>
                  setPreviewMode((m) => (m === "news" ? "chat" : "news"))
                }
              >
                {previewMode === "news" ? "Chats" : "News"}
              </button>
              <button
                type="button"
                style={{ backgroundColor: buttonSettings.background }}
                onClick={() => window.open(waLink(), "_blank")}
              >
                {widgetSettings.widgetCTA}
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          className="wa-widget-preview-btn"
          style={{
            backgroundColor: buttonSettings.background,
            borderRadius: `${buttonSettings.borderRadius}px`,
          }}
          onClick={() => setPreviewOpen(true)}
        >
          <FaWhatsapp /> {buttonSettings.ctaText}
        </button>
      </div>

      {newsModal && (
        <div
          className="wa-widget-snippet-modal"
          onClick={() => setNewsModal(false)}
          role="presentation"
        >
          <div
            className="wa-widget-news-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            {widgetSettings.newsImageUrl && (
              <img src={widgetSettings.newsImageUrl} alt="" />
            )}
            <h3>{widgetSettings.newsTitle}</h3>
            <p>{widgetSettings.newsText}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppWidgetButton;
